import once from 'lodash.once'
import {CorePlugin, type SupportedVersion} from '@clappr/core'
import type {Options} from 'common/types/options'
import {Events} from 'common/types/events'
import {Log} from 'utils/log'
import {loadScript} from 'utils/dom'
import {isDebug, isDevelopment} from 'utils/env'
import {type ComscoreConfig, AdvertisementBlockType} from 'common/api/records'
import type {
  Advertisement,
  AdvertisementEvent
} from 'common/plugins/advertisement'

export class Comscore<T extends Options> extends CorePlugin<T> {
  private static ADV_SCORE: Record<string, string> = {
    [AdvertisementBlockType.PRE_ROLL]: '09',
    [AdvertisementBlockType.PRE_ROLL_MOBILE]: '09',
    [AdvertisementBlockType.POST_ROLL]: '10',
    [AdvertisementBlockType.POST_ROLL_MOBILE]: '10',
    [AdvertisementBlockType.MIDDLE_ROLL]: '11',
    [AdvertisementBlockType.MIDDLE_ROLL_MOBILE]: '11',
    [AdvertisementBlockType.BANNER]: '12',
    [AdvertisementBlockType.BANNER_MOBILE]: '12'
  }

  public get name(): string {
    return 'comscore'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  public get params(): ComscoreConfig[] {
    return this.core.options.comscore || ([] as ComscoreConfig[])
  }

  private get advertisement(): Advertisement<T> | null {
    return this.core.getPlugin('advertisement') as Advertisement<T> | null
  }

  public bindEvents(): void {
    if (!this.core.isReady) {
      this.onPlay = once(this.onPlay)
    }

    this.listenToOnce(this.core, Events.CORE_READY, this.onReady)
    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.onActiveContainerChange
    )

    const {activeContainer} = this.core

    if (activeContainer) {
      this.listenToOnce(activeContainer, Events.CONTAINER_PLAY, this.onPlay)
    }

    if (this.advertisement) {
      // handle first loading from ad tag urls
      this.listenToOnce(
        this.advertisement,
        Events.ADVERTISEMENT_LOADING,
        this.onAdLoading
      )
    }
  }

  private onActiveContainerChange(): void {
    this.stopListening()
    this.bindEvents()
  }

  private async onReady(): Promise<void> {
    if (!window.COMSCORE && !isDevelopment()) {
      try {
        await loadScript('https://sb.scorecardresearch.com/beacon.js')
      } catch {}
    }
  }

  private onPlay(): void {
    this.params.forEach(({c2}) => this.beacon({c1: 1, c2, c5: '02'}))
  }

  private onAdLoading(event: AdvertisementEvent): void {
    const c5 = Comscore.ADV_SCORE[event.blockType] || '01'

    this.params.forEach(({c2}) => this.beacon({c1: 1, c2, c5}))
  }

  private beacon(params: Record<string, any>): void {
    if (isDebug()) {
      Log.debug('comscore', params)
    }

    if (isDevelopment()) {
      return
    }

    window.COMSCORE?.beacon(params)
  }
}
